import type { ServerSeatResponse } from 'api/flights'

type ServerSeatMapEntry = ServerSeatResponse['seatMap'][number]

export function seatsAvailabilityMap(serverResponse: ServerSeatResponse): App.FlightsSeatsAvailability {
  return {
    seatMap: serverResponse.seatMap?.map(mapSeatMapEntry) ?? [],
    metadata: mapMetadata(serverResponse.metadata),
  }
}

function mapSeatMapEntry(entry: ServerSeatMapEntry): App.FlightsSeatsSeatMapEntry {
  return {
    paxSegmentRefID: entry.paxSegmentRefID,
    datedOperatingLegRefID: entry.datedOperatingLegRefID ?? '',
    cabinCompartments: entry.cabinCompartments?.map(mapCabinCompartment) ?? [],
    fareId: entry.fareId ?? '',
    flightId: entry.flightId ?? '',
  }
}

function mapCabinCompartment(cabin: any): App.FlightsSeatsCabinCompartment {
  return {
    deckCode: cabin.deckCode ?? '',
    firstRow: cabin.firstRow ?? 0,
    lastRow: cabin.lastRow ?? 0,
    cabinType: {
      cabinTypeCode: cabin.cabinType?.cabinTypeCode ?? '',
      cabinTypeName: cabin.cabinType?.cabinTypeName ?? '',
      cabinTypeIataCode: cabin.cabinType?.cabinTypeName,
    },
    cabinLayout: {
      columns: cabin.cabinLayout?.columns?.map((col: any) => ({
        id: col.id,
        position: col.position,
      })) ?? [],
      rows: {
        firstRow: cabin.cabinLayout?.rows?.firstRow ?? 0,
        lastRow: cabin.cabinLayout?.rows?.lastRow ?? 0,
      },
      exitRowPosition: cabin.cabinLayout?.exitRowPosition?.map((exit: any) => ({
        firstRow: exit.firstRow,
        lastRow: exit.lastRow,
      })),
      facilities: cabin.cabinLayout?.facilities?.map((facility: any) => ({
        location: {
          beginRow: facility.location?.beginRow ?? '',
          endRow: facility.location?.endRow ?? '',
          columnPositions: facility.location?.columnPositions ?? [],
          orientation: facility.location?.orientation ?? '',
          positionCodeList: facility.location?.positionCodeList ?? [],
          positionCode: facility.location?.positionCode ?? '',
        },
        facilityType: facility.facilityType ?? '',
      })),
      seatCount: cabin.cabinLayout?.seatCount ?? 0,
      missingRowNumbers: cabin.cabinLayout?.missingRowNumbers,
      missingSeatList: cabin.cabinLayout?.missingSeatList,
    },
    seatRows: cabin.seatRows?.map(mapSeatRow) ?? [],
  }
}

function mapSeatRow(row: any): App.FlightsSeatsCabinCompartment['seatRows'][number] {
  return {
    row: row.row,
    characteristics: row.characteristics?.map(mapSeatCharacteristic) ?? [],
    seats: row.seats?.map(mapSeatData) ?? [],
    couch: row?.couch ? mapCouchConfig(row.couch) : undefined,
  }
}

function mapCouchConfig(couch: any): App.FlightsSeatsCouchConfig {
  return {
    couchColumnStart: couch.couchColumnStart ?? '',
    couchColumnEnd: couch.couchColumnEnd ?? '',
    availability: couch.availability,
    price: couch.price ? {
      totalAmount: {
        amount: couch.price.totalAmount?.amount ?? 0,
        currencyCode: couch.price.totalAmount?.currencyCode ?? '',
      },
    } : undefined,
  }
}

// function to map seat and row characteristics
function mapSeatCharacteristic(characteristic: any): App.FlightsSeatsCabinCompartment['seatRows'][number]['seats'][number]['characteristics'][number] {
  return {
    code: characteristic.code,
    description: characteristic.description ?? '',
  }
}

function mapSeatData(seat: any): App.FlightsSeatsItem {
  return {
    column: seat.column,
    seatNumber: seat.seatNumber ?? `${seat.row}${seat.column}`,
    occupationStatusCode: seat.occupationStatusCode ?? '',
    characteristics: seat.characteristics?.map(mapSeatCharacteristic) ?? [],
    characteristicsDescriptions: seat.characteristicsDescriptions,
    seatProfileRefIDs: seat.seatProfileRefIDs,
    offerItemRefIDs: seat.offerItemRefIDs,
    unitPrice: seat.unitPrice ? mapSeatPrice(seat.unitPrice) : undefined,
    pricesByPassengerType: mapPricesByPassengerType(seat.pricesByPassengerType),
  }
}

function mapSeatPrice(price: any): App.FlightsSeatsItem['unitPrice'] {
  return {
    totalAmount: {
      amount: price.totalAmount?.amount ?? 0,
      currencyCode: price.totalAmount?.currencyCode ?? '',
    },
    baseAmount: price.baseAmount ? {
      amount: price.baseAmount.amount,
      currencyCode: price.baseAmount.currencyCode,
    } : undefined,
  }
}

function mapPricesByPassengerType(
  pricesByPassengerType: Record<string, App.FlightsSeatPrice> | undefined
): App.FlightsSeatsItem['pricesByPassengerType'] {
  if (!pricesByPassengerType || Object.keys(pricesByPassengerType).length === 0) {
    return undefined
  }

  const result: Partial<Record<App.FlightPassengerType, App.FlightsSeatPrice>> = {}

  const validPassengerTypes: Array<App.FlightPassengerType> = ['adult', 'child', 'infant']

  for (const passengerType of validPassengerTypes) {
    if (pricesByPassengerType[passengerType]) {
      result[passengerType] = pricesByPassengerType[passengerType]
    }
  }

  return Object.keys(result).length > 0 ? result : undefined
}

function mapMetadata(metadata: any): App.FlightSeatsMetadata {
  return {
    originDests: metadata?.originDests?.map(mapOriginDest) ?? [],
    paxSegments: metadata?.paxSegments?.map(mapPaxSegment) ?? [],
  }
}

function mapOriginDest(originDest: any): App.FlightSeatsOriginDest {
  return {
    destCode: originDest.destCode ?? '',
    originCode: originDest.originCode ?? '',
    originDestID: originDest.originDestID ?? '',
  }
}

function mapPaxSegment(paxSegment: any): App.FlightSeatsPaxSegment {
  return {
    arrival: {
      iataLocationCode: paxSegment.arrival?.iataLocationCode ?? '',
      aircraftScheduledDateTime: paxSegment.arrival?.aircraftScheduledDateTime,
    },
    departure: {
      iataLocationCode: paxSegment.departure?.iataLocationCode ?? '',
      aircraftScheduledDateTime: paxSegment.departure?.aircraftScheduledDateTime,
    },
    operatingCarrierInfo: {
      carrierDesignationCode: paxSegment.operatingCarrierInfo?.carrierDesignationCode ?? '',
    },
    marketingCarrierInfo: {
      carrierDesignationCode: paxSegment.marketingCarrierInfo?.carrierDesignatorCode ?? '',
    },
    paxSegmentID: paxSegment.paxSegmentID ?? '',
  }
}
